import { useGetServiceProvidersQuery } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import type {
  FetchServiceProviderListParams,
  ServiceProviderType
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export const SELECTED_SERVICE_PROVIDER_PARAMETER_KEY = 'selectedServiceProvider'

function useServiceProvidersWithParams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [searchQueryParams, setSearchQueryParams] = useState<FetchServiceProviderListParams>({
    active:
      searchParams.get('active') === 'true'
        ? true
        : searchParams.get('active') === 'false'
          ? false
          : undefined,
    name: searchParams.get('name') ?? undefined,
    search_text: searchParams.get('search_text') ?? undefined,
    type: (searchParams.get('type') as ServiceProviderType) ?? undefined,
    sort_by: searchParams.get('sort_by') ?? 'serviceProviderId',
    sort_order: searchParams.get('sort_order') ?? SORT_ORDER_ASC,
    start_index: searchParams.get('start_index') ?? undefined,
    count: ROWS_PER_PAGE_DEFAULT
  })

  const selectedServiceProviderQueryParam =
    searchParams.get(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY) ?? undefined
  const [selectedServiceProviderId, setSelectedServiceProviderId] = useState(
    selectedServiceProviderQueryParam
  )

  const {
    data: { resources: serviceProviders } = { resources: [] },
    data: { total_results: totalResults } = { total_results: 0 },
    isFetching
  } = useGetServiceProvidersQuery(searchQueryParams)

  const setQueryParams = () => {
    Object.entries(searchQueryParams).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value.toString())
      } else {
        searchParams.delete(key)
      }
    })

    selectedServiceProviderId &&
      searchParams.set(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY, selectedServiceProviderId)

    setSearchParams(searchParams)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: not needed because we only want to run this when params change
  useEffect(() => {
    setQueryParams()
  }, [searchQueryParams])

  // biome-ignore lint/correctness/useExhaustiveDependencies: not needed because we only want to run this when selectedServiceProviderId changes
  useEffect(() => {
    if (selectedServiceProviderId) {
      setQueryParams()
    } else {
      searchParams.delete(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY)
      setSearchParams(searchParams)
    }
  }, [selectedServiceProviderId])

  return {
    serviceProviders,
    isFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    selectedServiceProviderId,
    setSelectedServiceProviderId
  }
}

export default useServiceProvidersWithParams
